/**
 * QA-79: the orgus token command for this hub's own home, from GET /api/signin-hint. A plain
 * `orgus token` targets the default home and is refused by a hub started with another
 * ORGUS_HOME. Null until it answers, on a hub beyond loopback, and when it gives no hint: the
 * page keeps today's words then. The control plane signs in through GitHub: never asked there.
 */
import { useEffect, useState } from 'react';
import { parseSigninHint } from '@orgus/protocol';
import type { SigninHintCommands } from '@orgus/protocol';

/** Demo data for the mock page: a home that is not the default one. */
export const MOCK_SIGNIN_HINT: SigninHintCommands = {
  powershell: "$env:ORGUS_HOME='C:\\Users\\you\\.orgus-main\\user-4600'; orgus token",
  bash: "ORGUS_HOME='/home/you/.orgus-main/user-4600' orgus token",
};

export async function fetchSigninHint(fetchFn: typeof fetch = fetch): Promise<SigninHintCommands | null> {
  try {
    const res = await fetchFn('/api/signin-hint', { headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(5_000) });
    return res.ok ? parseSigninHint(await res.json()).commands : null;
  } catch {
    return null;
  }
}

export function useSigninHint(skip: boolean, mock: boolean): SigninHintCommands | null {
  const [hint, setHint] = useState<SigninHintCommands | null>(mock && !skip ? MOCK_SIGNIN_HINT : null);
  useEffect(() => {
    if (skip || mock) return;
    let live = true;
    void fetchSigninHint().then((h) => live && setHint(h));
    return () => {
      live = false;
    };
  }, [skip, mock]);
  return skip ? null : hint;
}
